import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Shimmer } from "@/components/Shimmer";
import { toast } from "sonner";

export default function SettingsPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setData(r.data)); }, []);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        classifier_prompt: data.classifier_prompt,
        sales_categories: data.sales_categories,
        default_stage: data.default_stage,
        default_lead_source: data.default_lead_source,
        close_date_offset_days: Number(data.close_date_offset_days),
        test_mode_signature_prospect: data.test_mode_signature_prospect,
        large_file_threshold_bytes: Number(data.large_file_threshold_bytes),
        manual_minutes_per_opp: Number(data.manual_minutes_per_opp),
        pii_redaction_enabled: data.pii_redaction_enabled,
      };
      const r = await api.put("/settings", payload);
      setData(r.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-8"><Shimmer className="h-96" /></div>;

  return (
    <div className="p-8 max-w-[900px]" data-testid="settings-page">
      <PageHeader
        eyebrow="Admin"
        title="Settings"
        subtitle="Hot-reloaded by the running agent. Every change audit-logged."
        actions={<Button data-testid="settings-save-btn" onClick={save} disabled={busy} className="bg-zinc-900 hover:bg-zinc-800 text-white">Save</Button>}
      />

      <div className="space-y-6">
        <Section title="Classifier">
          <div>
            <Label className="overline">System prompt</Label>
            <Textarea data-testid="setting-classifier-prompt"
              value={data.classifier_prompt || ""}
              onChange={(e) => update("classifier_prompt", e.target.value)}
              className="mt-1 font-mono text-xs h-32"
            />
          </div>
          <div>
            <Label className="overline">Sales categories (comma-separated)</Label>
            <Input data-testid="setting-categories"
              value={(data.sales_categories || []).join(", ")}
              onChange={(e) => update("sales_categories", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
              className="mt-1 font-mono text-xs"
            />
          </div>
        </Section>

        <Section title="Opportunity defaults">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Default stage</Label>
              <Input data-testid="setting-default-stage"
                value={data.default_stage || ""} onChange={(e) => update("default_stage", e.target.value)}
                className="mt-1 font-mono text-xs"
              />
            </div>
            <div>
              <Label className="overline">Default lead source</Label>
              <Input data-testid="setting-lead-source"
                value={data.default_lead_source || ""} onChange={(e) => update("default_lead_source", e.target.value)}
                className="mt-1 font-mono text-xs"
              />
            </div>
            <div>
              <Label className="overline">Close date offset (days)</Label>
              <Input type="number" data-testid="setting-close-offset"
                value={data.close_date_offset_days ?? 0} onChange={(e) => update("close_date_offset_days", e.target.value)}
                className="mt-1 font-mono text-xs"
              />
            </div>
            <div>
              <Label className="overline">Manual minutes per opp</Label>
              <Input type="number" data-testid="setting-minutes-per-opp"
                value={data.manual_minutes_per_opp ?? 0} onChange={(e) => update("manual_minutes_per_opp", e.target.value)}
                className="mt-1 font-mono text-xs"
              />
            </div>
            <div>
              <Label className="overline">Loaded hourly rate (USD) · used for ROI</Label>
              <Input type="number" step="0.01" data-testid="setting-hourly-rate"
                value={data.loaded_hourly_rate_usd ?? 60} onChange={(e) => update("loaded_hourly_rate_usd", e.target.value)}
                className="mt-1 font-mono text-xs"
              />
            </div>
          </div>
        </Section>

        <Section title="File handling & privacy">
          <div>
            <Label className="overline">Large file threshold (bytes)</Label>
            <Input type="number" data-testid="setting-large-threshold"
              value={data.large_file_threshold_bytes ?? 0} onChange={(e) => update("large_file_threshold_bytes", e.target.value)}
              className="mt-1 font-mono text-xs"
            />
          </div>
          <SwitchRow label="Test mode · signature prospect parsing"
            value={data.test_mode_signature_prospect}
            onChange={(v) => update("test_mode_signature_prospect", v)}
            testId="setting-test-mode"
          />
          <SwitchRow label="PII redaction (regex for emails & phones)"
            value={data.pii_redaction_enabled}
            onChange={(v) => update("pii_redaction_enabled", v)}
            testId="setting-pii-redaction"
          />
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="card-surface p-5 space-y-4">
      <h2 className="font-serif text-xl tracking-tight">{title}</h2>
      {children}
    </div>
  );
}

function SwitchRow({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-2">
      <Label className="text-sm">{label}</Label>
      <Switch data-testid={testId} checked={!!value} onCheckedChange={onChange} />
    </div>
  );
}
